'use client'

import React, { useEffect, useRef, useState } from 'react'

interface AnimatedCounterProps {
  target: number
  duration?: number
  suffix?: string
  prefix?: string
}

function easeOutCubic(t: number): number {
  return 1 - (1 - t) ** 3
}

export function AnimatedCounter({
  target,
  duration = 1500,
  suffix = '',
  prefix = '',
}: AnimatedCounterProps) {
  const [value, setValue] = useState(0)
  const containerRef = useRef<HTMLSpanElement>(null)
  const startedRef = useRef(false)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !startedRef.current) {
          startedRef.current = true
          const startTime = performance.now()

          const tick = (now: number) => {
            const elapsed = now - startTime
            const progress = Math.min(elapsed / duration, 1)
            const easedProgress = easeOutCubic(progress)
            setValue(Math.round(easedProgress * target))
            if (progress < 1) requestAnimationFrame(tick)
          }

          requestAnimationFrame(tick)
          observer.disconnect()
        }
      },
      { threshold: 0.3 },
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [target, duration])

  const formatted = value.toLocaleString('pt-BR')

  return (
    <span ref={containerRef} aria-label={`${prefix}${target.toLocaleString('pt-BR')}${suffix}`}>
      {prefix}
      {formatted}
      {suffix}
    </span>
  )
}
